import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, UserPlus, Pencil } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventMemberRole, EventMemberStatus, EventTeamMember } from '../../types/eventTeam';
import { EVENT_MEMBER_ROLES, EVENT_MEMBER_STATUSES } from '../../types/eventTeam';
import { PlayerProfile } from '../PlayerProfile';
import { PlayerSearchField, type PlayerSelection } from './PlayerSearchField';

export interface EventMemberModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  member?: EventTeamMember | null;
  onClose: () => void;
  onSuccess: (member: EventTeamMember) => void;
}

function parseSpecialties(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
}

/** Roster CRUD modal (admin — guide §1.7). Specialties stored as a JSON array. */
export function EventMemberModal({ isOpen, mode, member, onClose, onSuccess }: EventMemberModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [player, setPlayer] = useState<PlayerSelection | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<EventMemberRole>('organizer');
  const [status, setStatus] = useState<EventMemberStatus>('active');
  const [specialtiesText, setSpecialtiesText] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (mode === 'edit' && member) {
      setPlayer({ uuid: member.minecraft_uuid, name: member.display_name });
      setDisplayName(member.display_name);
      setRole(member.role);
      setStatus(member.status);
      setSpecialtiesText(parseSpecialties(member.specialties).join(', '));
      setNotes(member.notes ?? '');
    } else {
      setPlayer(null);
      setDisplayName('');
      setRole('organizer');
      setStatus('active');
      setSpecialtiesText('');
      setNotes('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const specialties = specialtiesText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const specialtiesJson = specialties.length > 0 ? JSON.stringify(specialties) : null;

      if (mode === 'create') {
        if (!player || !displayName.trim()) {
          setError('Player and display name are required.');
          setIsSubmitting(false);
          return;
        }
        const created = await eventTeamApiService.createTeamMember({
          minecraft_uuid: player.uuid,
          display_name: displayName.trim(),
          role,
          status,
          specialties: specialtiesJson,
          notes: notes.trim() || null,
        });
        onSuccess(created);
      } else if (member) {
        const updated = await eventTeamApiService.updateTeamMember(member.id, {
          display_name: displayName.trim(),
          role,
          status,
          specialties: specialtiesJson,
          notes: notes.trim() || null,
        });
        onSuccess(updated);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4 flex items-center gap-2">
          {mode === 'create' ? (
            <>
              <UserPlus className="w-6 h-6 text-accent-400" />
              Add team member
            </>
          ) : (
            <>
              <Pencil className="w-6 h-6 text-accent-400" />
              Edit team member
            </>
          )}
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'create' ? (
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Player *</label>
              <PlayerSearchField
                value={player}
                onChange={(sel) => {
                  setPlayer(sel);
                  if (sel?.name && !displayName.trim()) setDisplayName(sel.name);
                }}
                autoFocus
              />
            </div>
          ) : (
            member && (
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Player</label>
                <PlayerProfile uuid={member.minecraft_uuid} />
              </div>
            )
          )}

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Display name *</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="glass-input w-full text-sm"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as EventMemberRole)}
                className="glass-input w-full text-sm capitalize"
              >
                {EVENT_MEMBER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as EventMemberStatus)}
                className="glass-input w-full text-sm capitalize"
              >
                {EVENT_MEMBER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Specialties</label>
            <input
              type="text"
              value={specialtiesText}
              onChange={(e) => setSpecialtiesText(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="comma separated, e.g. pvp, building, hosting"
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="glass-input w-full text-sm"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !displayName.trim() || (mode === 'create' && !player)}
              className="glass-button text-sm inline-flex items-center gap-1.5"
            >
              {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
              {mode === 'create' ? 'Add member' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
